import { useEffect, useState } from 'react'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { adminApi } from '@/utils/api'
import type { AdminFeedback, AdminReflection } from '@/types/admin'

export default function AdminContentPage() {
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const [feedback, setFeedback] = useState<AdminFeedback[]>([])
  const [reflections, setReflections] = useState<AdminReflection[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setError(null)
        const [feedbackRes, reflectionsRes] = await Promise.all([
          adminApi.contentFeedback(),
          adminApi.contentReflections(),
        ])
        setFeedback((feedbackRes as { data?: AdminFeedback[] }).data || (feedbackRes as unknown as AdminFeedback[]))
        setReflections(
          (reflectionsRes as { data?: AdminReflection[] }).data || (reflectionsRes as unknown as AdminReflection[])
        )
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load content')
      }
    }
    void load()
  }, [])

  const handleDeleteReflection = async (id: string) => {
    try {
      await adminApi.deleteReflection(id)
      setReflections((prev) => prev.filter((r) => r.id !== id))
    } catch (err) {
      setError((err as Error)?.message || 'Unable to delete reflection')
    }
  }

  return (
    <div className='min-h-screen bg-[#edeff3]'>
      <div className='p-4 md:p-6 lg:p-8'>
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 space-y-6'>
          {/* Header */}
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>Content</h1>
              <p className='text-[13px] sm:text-[14px] text-[#4e4d4d]'>Moderate feedback and reflections.</p>
            </div>
            <div className='hidden lg:flex items-center gap-3'>
              <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-[16px]'>
                {initials}
              </div>
              <span className='text-[24px] font-bold text-[#353535]'>{displayName}</span>
            </div>
          </div>

          {/* Feedback & Reflections Grid */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            {/* Couple Feedback */}
            <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6 space-y-4'>
              <h3 className='text-[18px] font-bold text-[#151515]'>Couple Feedback</h3>
              {feedback.length === 0 && (
                <p className='text-[14px] text-[#4e4d4d]'>No feedback available.</p>
              )}
              {feedback.map((item) => (
                <div key={item.id} className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
                  <p className='text-[12px] font-bold text-[#4e4d4d] mb-1'>{item.id}</p>
                  <p className='text-[14px] text-[#151515]'>{item.message}</p>
                  <div className='mt-3 flex gap-2'>
                    <button className='px-3 py-1 rounded-[8px] bg-[rgba(237,239,243,0.5)] text-[#151515] hover:bg-[rgba(237,239,243,0.8)] text-[12px] font-medium'>
                      Keep
                    </button>
                    <button className='px-3 py-1 rounded-[8px] bg-red-50 text-red-600 hover:bg-red-100 text-[12px] font-medium'>
                      Flag
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Video Reflections */}
            <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6 space-y-4'>
              <h3 className='text-[18px] font-bold text-[#151515]'>Video Reflections</h3>
              {reflections.length === 0 && (
                <p className='text-[14px] text-[#4e4d4d]'>No reflections available.</p>
              )}
              {reflections.map((item) => (
                <div key={item.id} className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
                  <p className='text-[12px] font-bold text-[#4e4d4d] mb-1'>{item.id}</p>
                  <p className='text-[14px] text-[#151515]'>{item.message}</p>
                  <div className='mt-3 flex gap-2'>
                    <button className='px-3 py-1 rounded-[8px] bg-[rgba(237,239,243,0.5)] text-[#151515] hover:bg-[rgba(237,239,243,0.8)] text-[12px] font-medium'>
                      Keep
                    </button>
                    <button
                      className='px-3 py-1 rounded-[8px] bg-red-50 text-red-600 hover:bg-red-100 text-[12px] font-medium'
                      onClick={() => void handleDeleteReflection(item.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && <p className='text-[14px] text-red-600'>{error}</p>}
        </div>
      </div>
    </div>
  )
}
